import type {Edge, Node} from "reactflow";
import {v4 as uuidv4} from "uuid";
import {AutomationNode, NodeConnection, NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {stepName, triggerNodeTypes} from "../config/nodeCategories";


export const toFlowNodes = (nodes: AutomationNode[] = []): Node[] => nodes.map(node => ({
    id: node.id,
    type: 'custom',
    position: {x: node.position.x, y: node.position.y},
    data: {...node},
}));

export const toFlowEdges = (connections: NodeConnection[] = [], defaults?: Partial<Edge>): Edge[] => (
    connections.map((connection, index) => ({
        ...defaults,
        id: `${connection.sourceNodeId}-${connection.targetNodeId}-${index}`,
        source: connection.sourceNodeId,
        target: connection.targetNodeId,
        sourceHandle: connection.sourceHandle,
        targetHandle: connection.targetHandle,
    }))
);

export const fromFlowNodes = (nodes: Node[] = []): AutomationNode[] => nodes.map(node => ({
    id: node.id,
    type: node.data.type,
    position: {x: node.position.x, y: node.position.y},
    config: node.data.config || {},
    label: node.data.label,
}));

export const fromFlowEdges = (edges: Edge[] = []): NodeConnection[] => edges.map(edge => ({
    sourceNodeId: edge.source,
    targetNodeId: edge.target,
    sourceHandle: edge.sourceHandle || undefined,
    targetHandle: edge.targetHandle || undefined,
}));


/**
 * Drops `nodeId`'s edges; with exactly one outgoing edge, each incoming edge (keeping its `sourceHandle`) is
 * joined to that successor. Healed edges take a fresh id — `removeNodesAndHeal` finds them by id — from
 * `uuid`, because jsdom has no `crypto.randomUUID`.
 */
export const reconnectAroundNode = (edges: Edge[], nodeId: string): Edge[] => {
    const incoming = edges.filter(edge => edge.target === nodeId);
    const outgoing = edges.filter(edge => edge.source === nodeId);
    const survivors = edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId);

    if (outgoing.length !== 1 || incoming.length === 0) return survivors;

    const [successor] = outgoing;
    const connection = (source: string, sourceHandle: string | null | undefined, target: string) =>
        `${source} ${sourceHandle ?? ''} ${target}`;
    const existing = new Set(survivors.map(edge => connection(edge.source, edge.sourceHandle, edge.target)));

    const healed: Edge[] = [];

    for (const edge of incoming) {
        // No self-loop on the successor, and no duplicate of an existing edge.
        if (edge.source === successor.target) continue;
        if (existing.has(connection(edge.source, edge.sourceHandle, successor.target))) continue;

        existing.add(connection(edge.source, edge.sourceHandle, successor.target));
        healed.push({
            ...successor,
            id: uuidv4(),
            source: edge.source,
            sourceHandle: edge.sourceHandle,
        });
    }

    return [...survivors, ...healed];
};

// React Flow removes the edges before it reports the nodes, so heal from `before`, the edges the delete started from.
export const removeNodesAndHeal = (current: Edge[], before: Edge[], nodeIds: string[]): Edge[] => {
    const removed = new Set(nodeIds);
    const known = new Set(before.map(edge => edge.id));
    const healed = nodeIds.reduce(reconnectAroundNode, before).filter(edge => !known.has(edge.id));

    return [
        ...current.filter(edge => !removed.has(edge.source) && !removed.has(edge.target)),
        ...healed,
    ];
};

export const stepLabel = (node: Node): string => stepName({label: node.data?.label?.trim(), type: node.data?.type});

// `label`, else `label 2`, `label 3`…: references bind by label, and the API refuses a shared one except between Stop steps.
export const uniqueStepLabel = (label: string, type: NodeType, nodes: Node[]): string => {
    const taken = (candidate: string) => nodes.some(node => stepLabel(node) === candidate
        && !(type === NodeType.FINISH && node.data?.type === NodeType.FINISH));

    const base = label.trim();
    if (!taken(base)) return label;

    let number = 2;
    while (taken(`${base} ${number}`)) number++;
    return `${base} ${number}`;
};

export const nodePositionsSignature = (nodes: Node[]): string => nodes
    .map(node => `${node.id}@${Math.round(node.position.x)},${Math.round(node.position.y)}`)
    .join('|');

// Breadth-first, nearest first: Send Email's default recipient depends on this order.
export const upstreamNodeData = (nodeId: string, nodes: Node[], edges: Edge[]): AutomationNode[] => {
    const nodesById = new Map(nodes.map(node => [node.id, node]));
    const seen = new Set<string>([nodeId]);
    const ordered: AutomationNode[] = [];

    let frontier = [nodeId];

    while (frontier.length) {
        const next: string[] = [];

        frontier.forEach(targetId => edges
            .filter(edge => edge.target === targetId)
            .forEach(edge => {
                if (seen.has(edge.source)) return;
                seen.add(edge.source);

                const node = nodesById.get(edge.source);
                if (node) ordered.push(node.data);

                next.push(edge.source);
            }));

        frontier = next;
    }

    return ordered;
};

// Everything `unreachableNodeIds` reads; a drag or a selection leaves it unchanged.
export const reachabilityKey = (nodes: Node[], edges: Edge[]): string => [
    nodes.map(node => `${node.id}:${node.data?.type}`).join('|'),
    edges.map(edge => `${edge.source}>${edge.target}`).join('|'),
].join('#');

// Steps no trigger reaches: they save and activate but never run. Triggers are never listed; every
// unvisited step is, loose root and all below it. Handles don't matter (If's Yes and No both run).
export const unreachableNodeIds = (nodes: Node[] = [], edges: Edge[] = []): Set<string> => {
    const isTrigger = (node: Node) => triggerNodeTypes.includes(node.data?.type);
    const targetsBySource = new Map<string, string[]>();
    edges.forEach(edge => {
        const targets = targetsBySource.get(edge.source) ?? [];
        targets.push(edge.target);
        targetsBySource.set(edge.source, targets);
    });

    const reached = new Set<string>();
    const queue = nodes.filter(isTrigger).map(node => node.id);
    while (queue.length) {
        const id = queue.shift()!;
        if (reached.has(id)) continue;
        reached.add(id);
        (targetsBySource.get(id) ?? []).forEach(target => queue.push(target));
    }

    return new Set(nodes.filter(node => !isTrigger(node) && !reached.has(node.id)).map(node => node.id));
};
